import type { ReactNode } from "react"
import { IconRefresh } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { OUTSIDE_BOOKING_SEASON_LABEL } from "../lib/bookingSeason"
import { SLOT_TYPE_LABEL } from "../lib/slotTypes"

export function ScheduleToolbar({
  onNavDay,
  onGoToday,
  onRefresh,
  refreshing = false,
  loading = false,
  teamLabel,
  dayLabel,
  isToday,
  bookingCount = 0,
  outsideWindowCount = 0,
  unhostedCount = 0,
  awayCount = 0,
  slotMinutes,
  unit = "day",
  viewToggle,
  todayLabel = "Today",
  summary,
}: {
  onNavDay: (delta: number) => void
  onGoToday: () => void
  /**
   * Re-read the grid from the server. Optional: a grid with no way to refetch gets no button
   * rather than a dead one.
   */
  onRefresh?: () => void
  /** A read is in flight, so the button spins and cannot be pressed again mid-flight. */
  refreshing?: boolean
  /**
   * The grid's contents are still on their way, so the counts below are not counts of anything yet.
   * Said instead of them: "0 appointments" over a grid of placeholders reads as a quiet day.
   */
  loading?: boolean
  /** The team whose day is on the grid, or the sites whose week it is. */
  teamLabel: string
  /** "Monday, Aug 17", or "Aug 17 – Aug 21" for a week. */
  dayLabel: string
  isToday: boolean
  /** Optional only for a grid that passes `summary` instead: it draws no appointments to count. */
  bookingCount?: number
  /** Appointments outside office hours, which the grid has no row for. */
  outsideWindowCount?: number
  /** Appointments hosted by nobody on the grid — their host has left the team. */
  unhostedCount?: number
  /** Team members with time nobody is working today, or days this person is not working. */
  awayCount?: number
  slotMinutes: number
  /**
   * What the arrows step by. The day view moves a day at a time and the staff week a week; the
   * buttons say which so a screen reader announces the move that actually happens.
   */
  unit?: "day" | "week"
  /**
   * A control switching what the grid draws — the staff calendar's week/day toggle. Beside the arrows
   * because it changes what they step through. Omitted on a grid with only one view.
   */
  viewToggle?: ReactNode
  /** The middle button's name: "Today" by default; the availability week says "This week". */
  todayLabel?: string
  /**
   * Drawn in place of the appointment counts, for a grid that shows no appointments (the
   * availability week, AB#803). Omitted everywhere else, which keeps the counts.
   */
  summary?: ReactNode
}) {
  const step = unit === "week" ? 7 : 1

  return (
    <div className="booking-calendar-toolbar">
      <div className="flex gap-1">
        <Button variant="outline" size="sm" onClick={() => onNavDay(-step)} aria-label={`Previous ${unit}`}>‹</Button>
        <Button variant="outline" size="sm" onClick={onGoToday}>{todayLabel}</Button>
        <Button variant="outline" size="sm" onClick={() => onNavDay(step)} aria-label={`Next ${unit}`}>›</Button>
      </div>
      {viewToggle}
      <div className="flex-1 flex items-center gap-2 text-[0.95rem] font-semibold">
        <span>{teamLabel}</span>
        <span className="text-muted-foreground font-normal">·</span>
        <span>{dayLabel}</span>
        {isToday && (
          <Badge variant="secondary" className="text-[0.7rem] font-semibold rounded">
            {unit === "week" ? "This week" : "Today"}
          </Badge>
        )}
      </div>
      {summary !== undefined ? (
        <div className="text-xs text-muted-foreground">{summary}</div>
      ) : loading ? (
        <div className="text-xs text-muted-foreground" role="status">
          Loading appointments… · {slotMinutes} min slots
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">
          {bookingCount} appointment{bookingCount === 1 ? "" : "s"} · {slotMinutes} min slots
          {/* The grid stops at close, so anything outside office hours is counted here instead of
              being quietly missing from the day. */}
          {outsideWindowCount > 0 && (
            <span className="text-amber-700"> · {outsideWindowCount} outside office hours</span>
          )}
          {/* Not folded into the count above: that one is an appointment at an awkward hour, this one
              is an appointment nobody on the team is down to take. Different thing to do about it. */}
          {unhostedCount > 0 && (
            <span className="text-amber-700"> · {unhostedCount} hosted by former staff</span>
          )}
          {/* Capacity, not trivia: a day that looks quiet because two of four staff are away is a
              different day from a quiet one, and the grid alone makes that a counting exercise. */}
          {awayCount > 0 && <span> · {awayCount} unavailable</span>}
        </div>
      )}
      {/* At the far end, away from the arrows: those change which day you are looking at and this
          one does not, and putting it in that group would make it look like a third way to move. */}
      {onRefresh && (
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={refreshing}
          aria-label={`Refresh this ${unit}`}
          title={`Refresh this ${unit}`}
        >
          <IconRefresh className={cn("size-4", refreshing && "animate-spin")} />
        </Button>
      )}
    </div>
  )
}

export function ScheduleLegend({ bookable = false, bookingSeason = false }: {
  /** Whether open slots on this grid can be clicked to book into. Changes what the key calls them. */
  bookable?: boolean
  /**
   * Whether the grid is marking slots outside the booking season — which it does only once the season
   * has been read. A key entry for a mark the grid is not drawing would only send somebody looking.
   */
  bookingSeason?: boolean
} = {}) {
  return (
    <div className="flex gap-4 flex-wrap px-5 py-2 bg-card border-b shrink-0">
      {[
        { style: { background: "var(--staff-1-light)", border: "1.5px solid var(--staff-1)" }, label: "Booked" },
        // Named for what it affords, not just what it is: to somebody who can book, a free row is an
        // invitation, and the key is where that is discoverable.
        {
          style: { background: "var(--slot-open)", border: "1.5px dashed var(--slot-open-border)" },
          label: bookable ? `${SLOT_TYPE_LABEL.public} open — click to book` : `${SLOT_TYPE_LABEL.public} open`,
        },
        // Beside "Open slot" because it is one: the same dashed edge, dotted over. Staff can still
        // book it; the key says who cannot.
        ...(bookingSeason
          ? [{
            style: {
              background:
                "radial-gradient(circle, var(--slot-outside-season-dot) 0.8px, transparent 1.2px) 0 0 / 4px 4px, "
                + "linear-gradient(var(--slot-outside-season-tint), var(--slot-outside-season-tint)), var(--slot-open)",
              border: "1.5px dashed var(--slot-open-border)",
            },
            label: OUTSIDE_BOOKING_SEASON_LABEL,
          }]
          : []),
        {
          style: {
            background:
              "repeating-linear-gradient(-45deg, var(--slot-unavail) 0 3px, var(--slot-unavail-stripe) 3px 6px)",
            border: "1.5px solid var(--slot-unavail-border)",
          },
          label: "Unavailable",
        },
        // Next to "Unavailable" on purpose: both are hatched, and the key is where somebody learns
        // that the two greys are different states rather than the same one drawn twice.
        {
          style: {
            background:
              "repeating-linear-gradient(-45deg, var(--slot-break) 0 3px, var(--slot-break-stripe) 3px 6px)",
            border: "1.5px solid var(--slot-break-border)",
          },
          label: "Break / lunch",
        },
        // The slot types (AB#779): public is the plain open and booked cells above, and each of the
        // other two is a pair, together, because the stripe is the same on both and what a reader
        // needs from the key is that the edge names the kind of appointment, not its state.
        {
          style: {
            background: "var(--slot-private-open)",
            border: "1.5px dashed var(--slot-open-border)",
            borderLeft: "3px solid var(--slot-private-edge)",
          },
          label: `${SLOT_TYPE_LABEL.private} open`,
        },
        { style: { background: "#f0fdfa", borderLeft: "3px solid var(--slot-private-edge)" }, label: `${SLOT_TYPE_LABEL.private} booked` },
        {
          style: {
            background: "var(--slot-offslot-open)",
            border: "1.5px dashed var(--slot-open-border)",
            borderLeft: "3px solid #dc2626",
          },
          label: `${SLOT_TYPE_LABEL.managed} open`,
        },
        { style: { background: "#fef2f2", borderLeft: "3px solid #dc2626" }, label: `${SLOT_TYPE_LABEL.managed} booked` },
        // One entry for both scopes: the right-hand edge means "somebody has since said they are not
        // there and this appointment is still on the books", whether that was one person's absence or
        // the whole site closing. The cell itself names which.
        {
          style: {
            background:
              "repeating-linear-gradient(-45deg, transparent 0 3px, var(--slot-away-stripe) 3px 6px) var(--staff-1-light)",
            borderRight: "3px solid var(--slot-conflict)",
          },
          label: "Booked during an absence",
        },
      ].map(item => (
        <div key={item.label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <div className="w-3 h-3 rounded-[3px] shrink-0" style={item.style} />
          {item.label}
        </div>
      ))}
    </div>
  )
}

/**
 * The grid before there is anything to cut it from — no roster yet, or no sites. Once there is, the
 * day and week views draw their own placeholders in the real grid's shape instead; this is only the
 * first load. Shimmers like those do, so the two read as the same wait.
 */
export function ScheduleLoadingSkeleton() {
  return (
    <div className="flex flex-col gap-3 p-5 flex-1" aria-busy role="status" aria-label="Loading appointments">
      <div className="grid gap-1.5" style={{ gridTemplateColumns: "72px repeat(4, 1fr)" }}>
        <div />
        {[...Array(4)].map((_, i) => <div key={i} className="slot-shimmer h-14 rounded-lg" />)}
      </div>
      {[...Array(6)].map((_, row) => (
        <div key={row} className="grid gap-1.5" style={{ gridTemplateColumns: "72px repeat(4, 1fr)" }}>
          <div className="slot-shimmer h-12 rounded-md" />
          {[...Array(4)].map((_, i) => <div key={i} className="slot-shimmer h-12 rounded-lg" />)}
        </div>
      ))}
    </div>
  )
}
